import type { MouseEventHandler, ReactNode } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import store, { RootState } from '~/store'
import { setDividerPosition } from '~/store/containerSlice'
import { cm } from '~/utils/utils'

export enum Orientation {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical'
}

type ContainerProps = {
  id: string
  firstChild: ReactNode
  firstClassName?: string
  secondChild?: ReactNode
  secondClassName?: string
  className: string | undefined
  orientation: Orientation
  initialSize: number
  isResizable: boolean
  resizeBarClassName?: string
  collapsed?: boolean
}

export const Container = ({
  id,
  firstChild,
  firstClassName,
  secondChild,
  secondClassName,
  className,
  orientation,
  initialSize,
  isResizable,
  resizeBarClassName,
  collapsed = false
}: ContainerProps): JSX.Element => {
  const dividerPosition: number =
    useSelector((state: RootState) => state.container[id]?.position) || initialSize
  const [firstSize, setFirstSize] = useState<number | undefined>(dividerPosition)
  const [isResizing, setIsResizing] = useState<boolean>(false)
  const firstRef = useRef<HTMLDivElement>(null)
  const resizeBarRef = useRef<HTMLDivElement>(null)
  const preCollapseSizeRef = useRef<number>(dividerPosition)

  useEffect(() => {
    if (isResizable && firstRef.current !== null) {
      if (orientation === Orientation.HORIZONTAL) {
        firstRef.current.style.width = `${firstSize}px`
      } else {
        firstRef.current.style.height = `${firstSize}px`
      }
    }
  }, [firstSize, orientation, isResizable])

  useEffect(() => {
    if (collapsed) {
      if (firstSize && firstSize > 0) {
        preCollapseSizeRef.current = firstSize
      }
      setFirstSize(0)
    } else {
      setFirstSize(preCollapseSizeRef.current || initialSize)
    }
  }, [collapsed]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateSize = useCallback(
    (newSize: number) => {
      if (firstRef.current === null) return
      const computedStyle = window.getComputedStyle(firstRef.current)

      // Use a default size if newSize is NaN
      const sizeToUse = isNaN(newSize) ? initialSize || 0 : newSize

      if (orientation === Orientation.HORIZONTAL) {
        const parentWidth = firstRef.current.parentElement?.clientWidth || 0
        const siblingMinWidth = Array.from(firstRef.current.parentElement?.children || [])
          .filter(child => child !== firstRef.current)
          .reduce((acc, child) => {
            const minWidth = parseFloat(window.getComputedStyle(child).minWidth)
            return acc + (isNaN(minWidth) ? 0 : minWidth)
          }, 0)

        const availableWidth = parentWidth - siblingMinWidth
        const minWidth = Math.max(parseFloat(computedStyle.minWidth) || 0, 0)
        const maxWidth = Math.min(
          isNaN(parseFloat(computedStyle.maxWidth)) ? Infinity : parseFloat(computedStyle.maxWidth),
          availableWidth
        )

        const newWidth = Math.max(minWidth, Math.min(sizeToUse, maxWidth))
        setFirstSize(newWidth)
        store.dispatch(setDividerPosition({ id, position: newWidth }))
      } else {
        const parentHeight = firstRef.current.parentElement?.clientHeight || 0
        const siblingMinHeight = Array.from(firstRef.current.parentElement?.children || [])
          .filter(child => child !== firstRef.current)
          .reduce((acc, child) => {
            const minHeight = parseFloat(window.getComputedStyle(child).minHeight)
            return acc + (isNaN(minHeight) ? 0 : minHeight)
          }, 0)

        const availableHeight = parentHeight - siblingMinHeight
        const minHeight = Math.max(parseFloat(computedStyle.minHeight) || 0, 0)
        const maxHeight = Math.min(
          isNaN(parseFloat(computedStyle.maxHeight))
            ? Infinity
            : parseFloat(computedStyle.maxHeight),
          availableHeight
        )

        const newHeight = Math.max(minHeight, Math.min(sizeToUse, maxHeight))
        setFirstSize(newHeight)
        store.dispatch(setDividerPosition({ id, position: newHeight }))
      }
    },
    [orientation, id, initialSize]
  )

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      if (firstRef.current === null) return
      const containerRect = firstRef.current.getBoundingClientRect()

      if (dividerPosition != null && !isNaN(dividerPosition)) {
        if (orientation === Orientation.HORIZONTAL) {
          const mouseX = e.clientX - containerRect.left
          const newLeftWidth = mouseX // This is the new position
          updateSize(newLeftWidth)
        } else {
          const mouseY = e.clientY - containerRect.top
          const newTopHeight = mouseY // This is the new position
          updateSize(newTopHeight)
        }
      } else {
        // If dividerPosition is invalid, use the current mouse position
        if (orientation === Orientation.HORIZONTAL) {
          updateSize(e.clientX - containerRect.left)
        } else {
          updateSize(e.clientY - containerRect.top)
        }
      }
    },
    [dividerPosition, orientation, updateSize]
  )

  useEffect(() => {
    if (isResizable) {
      const handleResize = () =>
        updateSize(firstSize != null && !isNaN(firstSize) ? firstSize : initialSize)
      window.addEventListener('resize', handleResize)
      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }
  }, [isResizable, updateSize, firstSize, initialSize])

  useEffect(() => {
    if (isResizable) {
      updateSize(firstSize != null && !isNaN(firstSize) ? firstSize : initialSize)
    }
  }, [isResizable, updateSize, firstSize, initialSize])

  const hoverClassnames = ['bg-blue-500', 'transition', 'delay-200']

  const onMouseUp = () => {
    document.body.style.cursor = 'auto'
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
    setIsResizing(false)
    resizeBarRef.current?.classList.remove(...hoverClassnames)
  }

  const onMouseDown = (e: MouseEvent) => {
    if (firstRef.current === null) return
    e.preventDefault()
    const containerRect = firstRef.current.getBoundingClientRect()

    const clientX = e.clientX - containerRect.left
    const clientY = e.clientY - containerRect.top

    const newDividerPosition = orientation === Orientation.HORIZONTAL ? clientX : clientY

    store.dispatch(setDividerPosition({ id, position: newDividerPosition }))
    document.body.style.cursor = orientation === Orientation.VERTICAL ? 'row-resize' : 'col-resize'
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    setIsResizing(true)
  }

  const onMouseOver = (e: MouseEvent) => {
    e.preventDefault()
    resizeBarRef.current?.classList.add(...hoverClassnames)
  }

  const onMouseLeave = (e: MouseEvent) => {
    e.preventDefault()
    if (!isResizing) {
      resizeBarRef.current?.classList.remove(...hoverClassnames)
    }
  }

  return (
    <div
      className={cm(
        `flex ${orientation === Orientation.HORIZONTAL ? 'flex-row' : 'flex-col'}`,
        'relative',
        className
      )}
    >
      <div
        ref={firstRef}
        className={cm(
          'first-child',
          firstClassName,
          isResizing || !secondChild ? '' : 'transition-all ease-in-out duration-500',
          secondChild ? '' : '!min-w-full'
        )}
      >
        {firstChild}
      </div>
      {isResizable && secondChild ? (
        <div
          ref={resizeBarRef}
          className={cm(
            'flex justify-start transition absolute z-50',
            `${orientation === Orientation.VERTICAL ? 'cursor-row-resize h-1' : 'cursor-col-resize w-1'} shrink-0`,
            orientation === Orientation.HORIZONTAL ? 'w-[1.5px] h-full' : 'w-full h-[1.5px]',
            resizeBarClassName
          )}
          style={{
            left: orientation === Orientation.HORIZONTAL ? `${firstSize! - 0.75}px` : '0',
            top: orientation === Orientation.VERTICAL ? `${firstSize! - 0.75}px` : '0'
          }}
          onMouseDown={onMouseDown as unknown as MouseEventHandler}
          onMouseOver={onMouseOver as unknown as MouseEventHandler}
          onMouseLeave={onMouseLeave as unknown as MouseEventHandler}
        >
          <div className={cm('z-20')}></div>
        </div>
      ) : null}
      {secondChild && (
        <div
          className={cm(
            secondClassName,
            `flex-1 ${orientation === Orientation.HORIZONTAL ? 'flex-row' : 'flex-col'}`,
            'transition-transform ease-in-out duration-500',
            secondChild ? '' : 'translate-x-full border-none'
          )}
        >
          {secondChild}
        </div>
      )}
    </div>
  )
}
